import type { Appearance } from '@jianghu/game-data';
import { useEffect, useRef } from 'react';
import * as THREE from 'three';
import { AnimationController } from '../game/animation/controller';
import { humanoidProfile } from '../game/animation/profiles';
import { HumanoidRig } from '../game/entities/humanoid';
import { playerLook } from '../game/entities/look';

interface Props {
  sect: string;
  appearance: Appearance;
  weapon: string | null;
  armor: string | null;
  caption?: string;
  subcaption?: string;
}

/** Small turntable renderer used on character select / creation. Drag to rotate. */
export function CharacterPreview({ sect, appearance, weapon, armor, caption, subcaption }: Props) {
  const host = useRef<HTMLDivElement>(null);
  const state = useRef<{
    setLook: (sect: string, a: Appearance, w: string | null, ar: string | null) => void;
  } | null>(null);

  useEffect(() => {
    const el = host.current;
    if (!el) return;
    let renderer: THREE.WebGLRenderer;
    try {
      renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    } catch {
      el.textContent = 'WebGL is not available.';
      return;
    }
    renderer.setPixelRatio(Math.min(2, window.devicePixelRatio));
    renderer.toneMapping = THREE.ACESFilmicToneMapping;
    renderer.shadowMap.enabled = true;
    el.prepend(renderer.domElement);
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(28, 1, 0.1, 50);
    camera.position.set(0, 1.3, 4.1);
    camera.lookAt(0, 0.92, 0);
    scene.add(new THREE.HemisphereLight('#c9d8ee', '#3a2f22', 1.3));
    const key = new THREE.DirectionalLight('#ffe7c4', 2.6);
    key.position.set(2.5, 4, 3);
    key.castShadow = true;
    key.shadow.mapSize.set(1024, 1024);
    scene.add(key);
    const rim = new THREE.DirectionalLight('#8cc1f2', 1.4);
    rim.position.set(-3, 2.5, -3);
    scene.add(rim);
    const ground = new THREE.Mesh(
      new THREE.CircleGeometry(1.4, 48),
      new THREE.MeshStandardMaterial({ color: '#2a2a33', roughness: 0.9 }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.receiveShadow = true;
    scene.add(ground);
    const ring = new THREE.Mesh(
      new THREE.RingGeometry(1.32, 1.4, 64),
      new THREE.MeshBasicMaterial({ color: '#c9a35a', transparent: true, opacity: 0.6 }),
    );
    ring.rotation.x = -Math.PI / 2;
    ring.position.y = 0.002;
    scene.add(ring);

    let rig: HumanoidRig | null = null;
    const anim = new AnimationController(humanoidProfile('sword'));
    let yaw = 0.35;
    let dragging = false;
    let lastX = 0;
    const setLook = (s: string, a: Appearance, w: string | null, ar: string | null): void => {
      if (rig) {
        scene.remove(rig.root);
        rig.dispose();
      }
      rig = new HumanoidRig(playerLook(s, a, w, ar));
      scene.add(rig.root);
    };
    state.current = { setLook };
    setLook(sect, appearance, weapon, armor);

    const resize = (): void => {
      const w = el.clientWidth || 300;
      const h = el.clientHeight || 400;
      renderer.setSize(w, h, false);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const ro = new ResizeObserver(resize);
    ro.observe(el);
    resize();
    const down = (e: PointerEvent): void => {
      dragging = true;
      lastX = e.clientX;
    };
    const move = (e: PointerEvent): void => {
      if (!dragging) return;
      yaw += (e.clientX - lastX) * 0.01;
      lastX = e.clientX;
    };
    const up = (): void => {
      dragging = false;
    };
    renderer.domElement.addEventListener('pointerdown', down);
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up);

    let raf = 0;
    let last = performance.now();
    let flourishAt = performance.now() + 2500;
    const frame = (now: number): void => {
      const dt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (!dragging) yaw += dt * 0.25;
      if (now > flourishAt) {
        anim.play(Math.random() < 0.5 ? 'ATTACK_1' : 'CAST_SLASH_WAVE', 0.9);
        flourishAt = now + 6000 + Math.random() * 3000;
      }
      if (rig) {
        rig.root.rotation.y = yaw;
        rig.applyPose(anim.update(dt, 0));
      }
      renderer.render(scene, camera);
      raf = requestAnimationFrame(frame);
    };
    raf = requestAnimationFrame(frame);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('pointermove', move);
      window.removeEventListener('pointerup', up);
      rig?.dispose();
      renderer.dispose();
      renderer.domElement.remove();
      state.current = null;
    };
    // The renderer is created once; look changes are applied through setLook below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    state.current?.setLook(sect, appearance, weapon, armor);
  }, [sect, appearance, weapon, armor]);

  return (
    <div className="preview" ref={host}>
      {caption && (
        <div className="caption">
          {caption}
          {subcaption && <small>{subcaption}</small>}
        </div>
      )}
    </div>
  );
}
